/**
 * BrushSelectionHandler
 * BRUSH Entity 타입의 선택 핸들러 로직
 */

import type { ILine } from '../../../backend/IRenderer'
import type { SelectionStrategy } from '../../base/selectionStrategy'
import type { BaseSelectionHandler } from '../../base/baseSelectionHandler'
import { CANVAS_ENTITY_EVENT } from '../../../const'

export class BrushSelectionHandler implements SelectionStrategy {
  /**
   * BRUSH 타입 선택 핸들러 추가
   * @param entity - BaseSelectionHandler 인스턴스
   */
  addHandlers(entity: BaseSelectionHandler): void {
    const systemLayer = entity.canvasEngine.getRenderer().layers.system
    const brush = entity.getBrush()
    if (!systemLayer || !brush) return

    const interactionEnabled =
      entity.getEntityInteractionMode() && entity.isSelected && !entity.canvasEngine.isPinchZooming

    // 브러시 그룹을 드래그 가능하게 설정
    brush.draggable(interactionEnabled)
    const targetNode = brush

    // Transformer 생성
    const shapeTransformer = entity.createSelectionHandles(
      {
        id: 'single-selection',
        padding: 20,
        anchorCornerRadius: 20,
        anchorSize: 20,
        rotateEnabled: interactionEnabled,
        resizeEnabled: interactionEnabled,
        borderStrokeWidth: 2,
        ignoreStroke: true,
        borderHitStrokeWidth: entity.TRANSFORMER_HIT_STROKE_WIDTH,
        draggable: interactionEnabled,
        anchorDraggable: interactionEnabled,
        anchorHitStrokeWidth: entity.TRANSFORMER_HIT_STROKE_WIDTH,
      },
      [targetNode],
    )
    entity.setShapeTransformer(shapeTransformer)

    systemLayer.add(shapeTransformer.node)
    shapeTransformer.moveToTop()
    systemLayer.batchDraw()

    // Transform 이벤트
    shapeTransformer.on('transform', () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) {
        // 멀티터치 중이면 transform 중단
        shapeTransformer.stopTransform()
        return
      }
      entity.canvasEngine.getRenderer().requestDraw()
      entity.hideButtonGroup()
    })

    // Transform End 이벤트
    shapeTransformer.on('transformend', () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) return
      entity.transformBrushShape()
      entity.canvasEngine.events.onEntityTransform(entity.getId())
      entity.showButtonGroup()
      entity.updateButtonPosition()
    })

    // Drag Start 이벤트
    const eventManager = entity.getEventManager()
    eventManager.removeEvent(targetNode, CANVAS_ENTITY_EVENT.SELECT_DRAG_START)
    eventManager.addEvent(targetNode, CANVAS_ENTITY_EVENT.SELECT_DRAG_START, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) {
        // 멀티터치 중이면 드래그 중단
        if (targetNode.isDragging()) {
          targetNode.stopDrag()
        }
        return
      }
      if (entity.balloon) {
        if (!entity.getRippleFollowMode()) {
          entity.removeShapeAnimation()
        }
      }
    })

    // Drag Move 이벤트
    eventManager.removeEvent(targetNode, CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE)
    eventManager.addEvent(targetNode, CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) return
      if (entity.balloon && entity.getRippleFollowMode()) {
        entity.updateRipplePosition()
      }
      entity.hideButtonGroup()
    })

    // Drag End 이벤트 (통합 핸들러)
    eventManager.removeEvent(targetNode, CANVAS_ENTITY_EVENT.SELECT_DRAG_END)
    eventManager.addEvent(targetNode, CANVAS_ENTITY_EVENT.SELECT_DRAG_END, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) return

      const brushX = brush.x()
      const brushY = brush.y()

      // 브러시 그룹의 위치를 Line들의 points에 적용
      if (brushX !== 0 || brushY !== 0) {
        const allLines = brush.find<ILine>('Line')
        allLines.forEach((line: ILine) => {
          const currentPoints = line.points()
          const newPoints = []
          for (let i = 0; i < currentPoints.length; i += 2) {
            newPoints.push(currentPoints[i] + brushX)
            newPoints.push(currentPoints[i + 1] + brushY)
          }
          line.points(newPoints)
        })

        // 브러시 그룹 위치 초기화
        brush.position({ x: 0, y: 0 })
      }

      // Balloon 위치 업데이트
      entity.updateBalloonPositionPublic()

      // Ripple 애니메이션 처리
      if (entity.balloon) {
        if (!entity.getRippleFollowMode()) {
          entity.addShapeAnimation()
        } else {
          entity.updateRipplePosition()
        }
      }

      // 히스토리 백업 (한 번만)
      entity.canvasEngine.events.onEntityTransform(entity.getId())

      // UI 업데이트
      entity.showButtonGroup()
      entity.updateButtonPosition()
    })
  }
}
